/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2024-11-06 10:38:39                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-01-23 16:24:06                               *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * Services
  * Components
  * CustomerSelect: customer select component
    * useEffect: Transform value to be printed in select
    * getCustomerList: Get customers list from api
    * customerChange: handle customer change event
    * handleCustomerCreate: handle customer creation on fly
*/

/* Imports */
import { useEffect, useState } from "react";
import AsyncSelect from "react-select/async";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { showApiErrorToast } from "../../../services/api/error";
/***/

/* Services */
import { api } from "../../../services/api/api";
import { setSelectList } from "../../../services/utils/utils";
import { createTextSearchQuery, searchWithFilters } from "../../../services/api/search";
/***/

/* Components */
import CustomerForm from "./CustomerForm";
import EditCreateDrawer from "../../edit-create-drawer/EditCreateDrawer";
import MultiCheckSelect from "../../option-select/MultiCheckSelect";
import { reactSelectTheme } from "../../option-select/reactSelectTheme";
/***/

/**
* CustomerSelect: customer select component
* Used to select a customer entity
* @param value - current value
* @param onChange - on value change event
* @param disabled - enabled/disabled state
* @param isClearable - allow field clear action
* @param isCreatable - allow on fly creation
* @param isEditable - allow on fly edition
* @param isMulti - allow multiple selection
* @param className - additional classnames
*/
const CustomerSelect = ({
  value,
  onChange,
  disabled,
  isClearable,
  isCreatable,
  isEditable,
  isMulti,
  className = ""
}: {
  value?: Partial<EasyTech.Customer> | Partial<EasyTech.Customer>[],
  onChange?: (value: any) => void,
  disabled?: boolean,
  className?: string,
  isClearable?: boolean,
  isCreatable?: boolean,
  isEditable?: boolean,
  isMulti?: boolean
}) => {
  const { t } = useTranslation();
  const [valueState, setValueState] = useState<any>(null);
  const [timestamp, setTimestamp] = useState<number>(Date.now());
  const [newCustomer, setNewCustomer] = useState<any>();

  /**
  * useEffect: Transform value to be printed in select 
  * @param value - reload on value change
  */
  useEffect(() => {
    if (value && !isMulti && (value as EasyTech.Customer).id) {
      setValueState({value: value, label: (value as EasyTech.Customer).name});
    } else if (value && isMulti && Array.isArray(value)) {
      const vals = [];
      const tmp = (value as EasyTech.Customer[]).filter(c => c && c.id);
      for (let i = 0; i < tmp.length; i++) {
        vals.push({value: tmp[i], label: tmp[i].name});
      }
      setValueState(vals);
    }
    else setValueState(null);
  }, [value, isMulti]);
  /***/

  /**
  * getCustomerList: Get customers list from api
  * @return - customer list ready to print in select 
  */
  const getCustomerList = (inputValue: string): Promise<EasyTech.IFormData<EasyTech.Customer>[]> => {
    return new Promise(resolve => {
      if (inputValue && inputValue.length > 0) {
        const searchOptions = {
          paginate: { pageSize: 100, pageNumber: 0 }, 
          queries: [createTextSearchQuery(["name", "nip"], inputValue)]
        };
        
        searchWithFilters<EasyTech.Customer>("customers", searchOptions, false, true).then(response => {
          if (newCustomer) {
            const c = response.data.find(el => el.id === newCustomer.id);
            if (c && onChange) onChange(c);
            setNewCustomer(null);
          }

          const sortedCustomers = response.data.filter((el) =>{
            const val: any = structuredClone(value);
            if (value && !isMulti && val.id && el.id === val.id) {
              return false;
            }

            return true;
          }).sort((a, b) => 
            a.name.localeCompare(b.name)
          );

          return resolve(
            setSelectList<EasyTech.Customer>(sortedCustomers, (el) => el.name)
          );
        }).catch((err) => {
          console.error(err);
          return resolve([]);
        });
      } else {
        api.get("admin/customers/search").then(ret => {
          if (newCustomer) {
            const c = (ret as EasyTech.Customer[]).find(el => el.id === newCustomer.id);
            if (c && onChange) onChange(c);
            setNewCustomer(null);
          }

          const sortedCustomers = (ret as EasyTech.Customer[]).filter((el) =>{
            const val: any = structuredClone(value);
            if (value && !isMulti && val.id && el.id === val.id) {
              return false;
            }

            return true;
          }).sort((a, b) => 
            a.name.localeCompare(b.name)
          );

          return resolve(
            setSelectList<EasyTech.Customer>(sortedCustomers, (el) => el.name)
          );
        }).catch((err) => {
          console.error(err);
          return resolve([]);
        });
      }
    });
  };
  /***/

  /**
  * customerChange: handle customer change event 
  * @param value - new value
  */
  const customerChange = (value: any) => {
    const ret = isMulti ? [] : (value ? value.value : value);
    if (isMulti) {
      for (let i = 0; i < value.length; i++) {
        ret.push(value[i] ? value[i].value : value[i]);
      }
    }
    setValueState(value);
    if (onChange) onChange(ret);
  };
  /***/

  /**
  * handleCustomerCreate: handle customer creation on fly
  * @param data - customer data 
  */
  const handleCustomerCreate = (data: EasyTech.Customer): Promise<void> => {
    return new Promise((resolve, reject) => {
      api.post("users/customers", data).then((ret) => {
        toast.success("Customer created !");
        
        // Auto-select the new customer
        if (onChange) onChange(ret);
        
        setNewCustomer(ret);
        setTimestamp(Date.now()); // Force reload customers list
        return resolve();
      }).catch((err) => {
        console.error(err);
        showApiErrorToast(err);
        return reject();
      });
    });
  };
  /***/

  const SelectField = isMulti ? MultiCheckSelect : AsyncSelect;

  return (
    <div className={`flex w-full min-w-0 items-center gap-2 ${className}`}>
      <SelectField  key={timestamp}
                    cacheOptions
                    defaultOptions
                    loadOptions={getCustomerList}
                    value={valueState}
                    theme={reactSelectTheme}
                    onChange={customerChange}
                    className="min-w-0 flex-1"
                    name="customer"
                    placeholder={t("common:selectCustomer")}
                    classNamePrefix="react-select"
                    isClearable={isClearable}
                    isDisabled={disabled}
                    isMulti={isMulti}
                    closeMenuOnSelect={!isMulti}
                    hideSelectedOptions={!isMulti} />

      <EditCreateDrawer<EasyTech.Customer>  FormComponent={CustomerForm}
                                            isNotEditable={!isEditable}
                                            isNotCreatable={!isCreatable}
                                            onSubmit={handleCustomerCreate}
                                            entityName="customer"/>
    </div>
  );
};

export default CustomerSelect;
